import { useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, useMutation } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Sparkles, Check, Loader2, Calendar, Receipt } from "lucide-react";
import { PLANS } from "@/lib/plans";
import { t, tf } from "@/lib/i18n";

const fmtDate = (s) => {
  if (!s) return "—";
  try {
    return new Date(s).toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" });
  } catch {
    return s;
  }
};

export default function Billing() {
  const { refreshUser } = useAuth();
  const [params, setParams] = useSearchParams();

  const { data: status, refetch, isLoading } = useQuery({
    queryKey: ["billing"],
    queryFn: async () => (await api.get("/billing/status")).data,
  });

  const { data: details } = useQuery({
    queryKey: ["billing-details"],
    queryFn: async () => (await api.get("/billing/details")).data,
  });

  const activate = useMutation({
    mutationFn: async () => (await api.post("/billing/activate")).data,
    onSuccess: async (d) => {
      if (d.active) {
        toast.success(t("Abbonamento attivato!"));
        await refreshUser();
        refetch();
      } else {
        toast.message(t("Abbonamento in attesa di conferma da PayPal."));
      }
    },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });

  const subscribe = useMutation({
    mutationFn: async (tier) => (await api.post(`/billing/subscribe?tier=${tier}`)).data,
    onSuccess: (d) => {
      window.location.href = d.approval_url;
    },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });

  useEffect(() => {
    if (params.get("status") === "success") {
      activate.mutate();
      params.delete("status");
      setParams(params, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const active = status?.active;
  const isTrial = status?.status === "trialing";

  return (
    <div className="space-y-6 max-w-5xl">
      <div>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight">{t("Abbonamento")}</h1>
        <p className="text-muted-foreground text-sm mt-1">{t("Gestisci il piano della tua azienda")}</p>
      </div>

      <div
        className={`rounded-2xl border p-5 flex items-center justify-between gap-4 ${
          active ? "bg-emerald-50 border-emerald-200" : "bg-amber-50 border-amber-200"
        }`}
        data-testid="billing-status-card"
      >
        <div>
          <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{t("Stato attuale")}</p>
          <p className="font-display text-xl font-bold mt-1">
            {isLoading
              ? "…"
              : status?.status === "active"
              ? t("Abbonamento attivo")
              : isTrial
              ? tf("Prova gratuita — {n} giorni rimasti", { n: status?.trial_days_left ?? 0 })
              : t("Abbonamento scaduto")}
          </p>
        </div>
        {active ? <Check className="h-8 w-8 text-emerald-600" /> : <Sparkles className="h-8 w-8 text-amber-600" />}
      </div>

      <div className="grid md:grid-cols-3 gap-5">
        {PLANS.map((p, i) => (
          <motion.div key={p.key} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
            className={`rounded-2xl bg-white p-6 shadow-sm border-t-4 border border-border flex flex-col ${status?.plan === p.key ? `ring-2 ${p.ring}` : ""}`}
            style={{ borderTopColor: p.color }} data-testid={`plan-${p.key}-card`}>
            <p className={`text-sm font-semibold ${p.text}`}>{p.name}</p>
            <p className="text-xs text-muted-foreground">{p.tag}</p>
            <p className="font-display text-4xl font-extrabold mt-2">{p.price}€<span className="text-base font-medium text-muted-foreground">{t("/mese")}</span></p>
            <ul className="mt-5 space-y-2 text-sm flex-1">
              {p.features.map((f) => (
                <li key={f} className="flex items-center gap-2"><Check className={`h-4 w-4 ${p.text}`} /> {f}</li>
              ))}
            </ul>
            <Button className={`w-full h-11 mt-6 font-semibold text-white ${p.btn}`} onClick={() => subscribe.mutate(p.key)}
              disabled={subscribe.isPending || status?.plan === p.key} data-testid={`subscribe-${p.key}-btn`}>
              {subscribe.isPending && subscribe.variables === p.key ? <Loader2 className="h-4 w-4 animate-spin" /> : status?.plan === p.key ? t("Piano attivo") : tf("Scegli {plan}", { plan: p.name })}
            </Button>
          </motion.div>
        ))}
      </div>
      <p className="text-xs text-muted-foreground text-center">{t("Pagamento sicuro gestito da PayPal. Puoi annullare quando vuoi.")}</p>

      {/* Renewal + payment history */}
      {details?.subscribed && (
        <div className="grid sm:grid-cols-5 gap-4">
          <div className="sm:col-span-2 rounded-2xl border border-border bg-white p-5" data-testid="next-renewal-card">
            <div className="flex items-center gap-2 text-muted-foreground">
              <Calendar className="h-4 w-4" />
              <span className="text-xs font-medium uppercase tracking-wider">{t("Prossimo rinnovo")}</span>
            </div>
            <p className="font-display text-xl font-bold mt-2">{fmtDate(details.next_billing_time)}</p>
            {details.paypal_status && (
              <p className="text-xs text-muted-foreground mt-1">{t("Stato PayPal:")} {details.paypal_status}</p>
            )}
          </div>

          <div className="sm:col-span-3 rounded-2xl border border-border bg-white p-5" data-testid="payments-history-card">
            <div className="flex items-center gap-2 text-muted-foreground mb-3">
              <Receipt className="h-4 w-4" />
              <span className="text-xs font-medium uppercase tracking-wider">{t("Storico pagamenti")}</span>
            </div>
            {details.payments?.length ? (
              <div className="divide-y divide-border">
                {details.payments.map((p) => (
                  <div key={p.id} className="flex items-center justify-between py-2 text-sm" data-testid={`payment-row-${p.id}`}>
                    <span className="text-muted-foreground">{fmtDate(p.date)}</span>
                    <span className="flex items-center gap-3">
                      <span className="font-semibold">{Number(p.amount || 0).toFixed(2)} {p.currency}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${p.status === "COMPLETED" ? "bg-emerald-100 text-emerald-700" : "bg-secondary text-muted-foreground"}`}>
                        {p.status}
                      </span>
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">{t("Nessun pagamento ancora registrato. Il primo addebito avverrà al termine della prova.")}</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
